import { useEffect, useRef, useState } from 'react';
import type { ClientRoomState, SpotifyAuthStatus } from '@hitster/shared';
import { useT } from '../i18n.tsx';
import { isMobileBrowser } from '../hooks/useSpotifyPlayer.ts';
import { useSpeaker } from '../hooks/SpeakerProvider.tsx';
import { useTickSound } from '../hooks/useTickSound.ts';
import { startSpotifyLogin } from '../net/spotify-auth.ts';
import type { RoomApi } from '../net/useRoom.ts';
import { Alert, Spinner } from './common.tsx';
import { IconClose, IconVolumeHigh, IconVolumeLow, IconVolumeOff } from './Icon.tsx';

/**
 * Ton-Steuerung als Ausklapp-Menue.
 *
 * Im Spiel ist der Platz knapp: das ausfuehrliche Lautsprecher-Panel
 * hat die Ansicht so verlaengert, dass man fuer die eigene Zeitleiste
 * scrollen musste. Die Steuerung sitzt deshalb kompakt in der Kopfzeile
 * und klappt nur bei Bedarf auf.
 */
export function SpeakerMenu({
  state,
  api,
  authStatus,
}: {
  state: ClientRoomState;
  api: RoomApi;
  authStatus: SpotifyAuthStatus | null;
}) {
  const t = useT();
  const player = useSpeaker();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  /**
   * Eindeutiges Feld vom Server -- NICHT aus `state.players` ableiten.
   *
   * Wer vom Mitspielen aufs Zuschauen wechselt, verschwindet aus
   * `players` und taucht in `spectators` auf. Eine Suche in `players`
   * meldet dann faelschlich "kein Lautsprecher", obwohl das Geraet
   * weiter Ton ausgibt.
   */
  const isSpeaker = state.youAreAudioDevice;
  const nobodyPlaying = state.audioDeviceCount === 0;

  // Klick nach draussen und Escape schliessen das Menue.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const Icon = isSpeaker
    ? player.volume === 0
      ? IconVolumeOff
      : IconVolumeHigh
    : nobodyPlaying
      ? IconVolumeOff
      : IconVolumeLow;

  return (
    <div className="dropdown" ref={containerRef}>
      <button
        type="button"
        className="btn btn--ghost"
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
        aria-haspopup="true"
        title={t('audio.title')}
        // Fehlt jeder Lautsprecher, muss das ins Auge fallen -- sonst
        // sitzt die Runde vor stiller Musik und sucht den Fehler.
        style={nobodyPlaying ? { color: 'var(--warning)' } : undefined}
      >
        <Icon />
        {isSpeaker && player.volume > 0 && (
          <span className="text-xs">{Math.round(player.volume * 100)}%</span>
        )}
        {nobodyPlaying && <span className="text-xs">!</span>}
      </button>

      {open && (
        <div className="dropdown__panel" role="dialog" aria-label={t('audio.title')}>
          <div className="stack stack--tight">
            <div className="row row--between">
              <span className="panel__title" style={{ margin: 0 }}>
                {t('audio.title')}
              </span>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setOpen(false)}
                aria-label={t('common.close')}
              >
                <IconClose />
              </button>
            </div>

            <SpeakerControls state={state} api={api} authStatus={authStatus} />

            {/*
             * Bewusst AUSSERHALB von SpeakerControls: das Geraeusch
             * kommt aus dem Browser, nicht von Spotify. Drinnen waere
             * es hinter den fruehen Abbruechen fuer Handy, fehlendes
             * Spotify oder fehlendes Premium verschwunden -- gerade
             * dort ist es aber die einzige Tonquelle.
             */}
            <TickToggle />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Der eigentliche Inhalt -- getrennt, damit ihn auch das ausfuehrliche
 * Panel in der Lobby verwenden kann.
 */
export function SpeakerControls({
  state,
  api,
  authStatus,
}: {
  state: ClientRoomState;
  api: RoomApi;
  authStatus: SpotifyAuthStatus | null;
}) {
  const t = useT();
  const player = useSpeaker();

  // Siehe SpeakerMenu: gilt unabhaengig von Spieler- oder Zuschauerrolle.
  const isSpeaker = state.youAreAudioDevice;

  if (isMobileBrowser()) {
    return (
      <div className="stack stack--tight">
        <Alert kind="info">{t('audio.mobileUnsupported')}</Alert>
        {state.audioDeviceCount === 0 && <Alert kind="warning">{t('audio.none')}</Alert>}
      </div>
    );
  }

  if (!authStatus?.linked) {
    return (
      <div className="stack stack--tight">
        <span className="text-sm text-muted">{t('audio.needSpotify')}</span>
        <button
          type="button"
          className="btn btn--spotify"
          onClick={() => startSpotifyLogin(window.location.pathname)}
        >
          {t('spotify.connect')}
        </button>
      </div>
    );
  }

  if (!authStatus.hasPremium) {
    return <Alert kind="warning">{t('spotify.premiumExplain')}</Alert>;
  }

  return (
    <div className="stack stack--tight">
      {player.status === 'loading' && (
        <div className="row">
          <Spinner />
          <span className="text-sm">{t('audio.initializing')}</span>
        </div>
      )}

      {player.status === 'error' && (
        <Alert kind="error">{t('audio.error', { message: player.errorMessage ?? '?' })}</Alert>
      )}

      {isSpeaker ? (
        <>
          <div className="row">
            <span className="badge badge--speaker">{t('audio.active')}</span>
          </div>

          <VolumeSlider volume={player.volume} onChange={player.setVolume} />

          <button
            type="button"
            className="btn btn--block"
            onClick={() => {
              api.setAudioEnabled(false);
              player.disable();
            }}
          >
            {t('audio.disable')}
          </button>
        </>
      ) : (
        <>
          <span className="text-sm text-muted">{t('audio.explain')}</span>
          <button
            type="button"
            className="btn btn--primary btn--block"
            disabled={player.status === 'loading'}
            // MUSS aus einem Klick kommen: activateElement() braucht
            // eine echte Nutzerinteraktion, sonst bleibt es stumm.
            onClick={() => {
              void player.enable();
            }}
          >
            {t('audio.enable')}
          </button>
        </>
      )}

      {state.audioDeviceCount > 0 ? (
        <span className="text-xs text-dim">
          {t('audio.count', { count: state.audioDeviceCount })}
        </span>
      ) : (
        <Alert kind="warning">{t('audio.none')}</Alert>
      )}

      <span className="text-xs text-dim">{t('audio.cheatWarning')}</span>
    </div>
  );
}

/**
 * Lautstaerkeregler fuer dieses Geraet.
 *
 * Wirkt nur lokal: bei Remote-Play hat jeder seinen eigenen Player und
 * regelt sich selbst. Der Wert ueberlebt einen Reload.
 */
export function VolumeSlider({
  volume,
  onChange,
}: {
  volume: number;
  onChange: (volume: number) => void;
}) {
  const t = useT();
  const percent = Math.round(volume * 100);

  return (
    <div className="field">
      <label className="field__label" htmlFor="speaker-volume">
        {t('audio.volume')}: {percent}%
      </label>
      <div className="row" style={{ gap: '0.5rem' }}>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => onChange(volume > 0 ? 0 : 0.8)}
          aria-label={volume > 0 ? t('audio.mute') : t('audio.unmute')}
          title={volume > 0 ? t('audio.mute') : t('audio.unmute')}
        >
          {volume === 0 ? (
            <IconVolumeOff />
          ) : percent < 40 ? (
            <IconVolumeLow />
          ) : (
            <IconVolumeHigh />
          )}
        </button>
        <input
          id="speaker-volume"
          type="range"
          min={0}
          max={100}
          step={1}
          value={percent}
          onChange={(event) => onChange(Number(event.target.value) / 100)}
          style={{ flex: 1, minWidth: 0, accentColor: 'var(--accent)' }}
          aria-label={t('audio.volume')}
        />
      </div>
    </div>
  );
}

/**
 * Schalter fuer den Klick zum laufenden Jahr.
 *
 * Eigene Zeile im Menue, weil es die einzige Tonausgabe ist, die ohne
 * Spotify und ohne Premium funktioniert -- auf dem Handy ist es sogar
 * die einzige ueberhaupt.
 */
function TickToggle() {
  const t = useT();
  const { enabled, setEnabled } = useTickSound();

  return (
    <label className="row" style={{ gap: '0.5rem', cursor: 'pointer' }}>
      <input
        type="checkbox"
        checked={enabled}
        onChange={(event) => setEnabled(event.target.checked)}
        style={{ accentColor: 'var(--accent)' }}
      />
      <span className="text-sm">{t('audio.tickSound')}</span>
    </label>
  );
}